import { SystemTranslationKeys } from '@common/entities';

import { formatNumber } from 'utils/format';

import { getFormattingSettings } from './formattingLocale';

export type UnitOfMeasure = {
  code?: string;
  name?: string;
};

export type PropertyUnitsOfMeasure = {
  area?: UnitOfMeasure;
  distance?: UnitOfMeasure;
  weight?: UnitOfMeasure;
};

type UnitKind = keyof PropertyUnitsOfMeasure;
type TranslateSystem = (key: string) => string;

export const DEFAULT_US_UNITS_OF_MEASURE: Record<UnitKind, Required<UnitOfMeasure>> = {
  area: { code: 'square_feet', name: 'Square Feet' },
  distance: { code: 'miles', name: 'Miles (mi)' },
  weight: { code: 'pounds', name: 'Pounds (lbs)' },
};

const LEGACY_UNIT_CODES: Record<UnitKind, Record<string, string>> = {
  area: {
    'square feet': 'square_feet',
    square_foot: 'square_feet',
    sq_ft: 'square_feet',
    sqft: 'square_feet',
    'square meters': 'square_meters',
    square_meter: 'square_meters',
    square_metres: 'square_meters',
    square_metre: 'square_meters',
    sq_m: 'square_meters',
    sqm: 'square_meters',
  },
  distance: {
    miles: 'miles',
    'miles (mi)': 'miles',
    kilometers: 'kilometers',
    'kilometers (km)': 'kilometers',
  },
  weight: {
    pounds: 'pounds',
    'pounds (lbs)': 'pounds',
    kilograms: 'kilograms',
    'kilograms (kg)': 'kilograms',
  },
};

const SUPPORTED_UNIT_CODES: Record<UnitKind, readonly string[]> = {
  area: ['square_feet', 'square_meters'],
  distance: ['miles', 'kilometers'],
  weight: ['pounds', 'kilograms'],
};

const UNIT_NAMES: Record<UnitKind, Record<string, string>> = {
  area: { square_feet: 'Square Feet', square_meters: 'Square Meters' },
  distance: { miles: 'Miles (mi)', kilometers: 'Kilometers (km)' },
  weight: { pounds: 'Pounds (lbs)', kilograms: 'Kilograms (kg)' },
};

function normalizeCode(code: string | undefined, kind: UnitKind): string | undefined {
  if (!code) return undefined;
  const normalized = code
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (normalized === 'kilometres') return 'kilometers';
  const canonicalCode = LEGACY_UNIT_CODES[kind][normalized] ?? normalized;
  return SUPPORTED_UNIT_CODES[kind].includes(canonicalCode) ? canonicalCode : undefined;
}

function getLegacyUnitSetting(kind: UnitKind): string | undefined {
  const settings = getFormattingSettings();
  if (kind === 'area') return settings?.unitOfMeasureForArea;
  if (kind === 'weight') return settings?.unitOfMeasureForWeight;
  return settings?.unitOfMeasureForDistance;
}

function resolveUnit(kind: UnitKind): UnitOfMeasure {
  const settings = getFormattingSettings();
  const configured = settings?.unitsOfMeasure?.[kind];
  const legacy = getLegacyUnitSetting(kind);
  const configuredCode = normalizeCode(configured?.code, kind);
  const legacyCode = normalizeCode(legacy, kind);
  const code = configuredCode ?? legacyCode ?? DEFAULT_US_UNITS_OF_MEASURE[kind].code;
  let name: string | undefined;
  if (configuredCode) name = configured?.name?.trim();
  else if (legacyCode) name = legacy?.trim();

  return {
    code,
    name: name || UNIT_NAMES[kind][code] || DEFAULT_US_UNITS_OF_MEASURE[kind].name,
  };
}

function convert(value: number, from: string | undefined, to: string | undefined): number {
  if (from === to) return value;
  if (from === 'square_feet' && to === 'square_meters') return value * 0.092903;
  if (from === 'square_meters' && to === 'square_feet') return value * 10.7639;
  if (from === 'pounds' && to === 'kilograms') return value * 0.45359237;
  if (from === 'kilograms' && to === 'pounds') return value * 2.20462;
  if (from === 'miles' && to === 'kilometers') return value * 1.609344;
  if (from === 'kilometers' && to === 'miles') return value * 0.621371;
  return value;
}

function format(
  value: number | null | undefined,
  unit: UnitOfMeasure,
  canonicalCode: string,
): string | null {
  if (value == null || !Number.isFinite(value) || value === 0) return null;
  const converted = convert(value, canonicalCode, unit.code);
  return formatNumber(Math.round(converted), { maximumFractionDigits: 0 });
}

const TRANSLATION_KEYS: Record<UnitKind, Record<string, string>> = {
  area: {
    square_feet: SystemTranslationKeys.common.unitSquareFeet,
    square_meters: SystemTranslationKeys.common.unitSquareMeters,
  },
  weight: {
    pounds: SystemTranslationKeys.common.unitPounds,
    kilograms: SystemTranslationKeys.common.unitKilograms,
  },
  distance: {
    miles: SystemTranslationKeys.common.unitMiles,
    kilometers: SystemTranslationKeys.common.unitKilometers,
  },
};

function getUnitLabel(kind: UnitKind, tSystem?: TranslateSystem): string {
  const settings = getFormattingSettings();
  const configured = settings?.unitsOfMeasure?.[kind];
  const legacy = getLegacyUnitSetting(kind);
  const configuredCode = normalizeCode(configured?.code, kind);
  const legacyCode = normalizeCode(legacy, kind);
  const configuredName = configuredCode ? configured?.name?.trim() : undefined;
  const legacyName = legacyCode ? legacy?.trim() : undefined;
  if (configuredName) return configuredName;
  if (legacyName && !configuredCode) return legacyName;

  const unit = resolveUnit(kind);
  const translationKey = unit.code ? TRANSLATION_KEYS[kind][unit.code] : undefined;
  return translationKey && tSystem
    ? tSystem(translationKey)
    : (unit.name ?? DEFAULT_US_UNITS_OF_MEASURE[kind].name);
}

export function getAreaUnitLabel(tSystem?: TranslateSystem): string {
  return getUnitLabel('area', tSystem);
}

export function getWeightUnitLabel(tSystem?: TranslateSystem): string {
  return getUnitLabel('weight', tSystem);
}

export function getDistanceUnitLabel(tSystem?: TranslateSystem): string {
  return getUnitLabel('distance', tSystem);
}

export function formatArea(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value) || value === 0) return null;
  return formatNumber(Math.round(value), { maximumFractionDigits: 0 });
}

export function formatAreaRange(
  min: number | null | undefined,
  max?: number | null,
): string | null {
  const formattedMin =
    min === 0 && max != null && Number.isFinite(max) && max > 0
      ? formatNumber(0, { maximumFractionDigits: 0 })
      : formatArea(min);
  const formattedMax = formatArea(max);
  if (!formattedMin && !formattedMax) return null;
  if (!formattedMax || formattedMin === formattedMax) return formattedMin ?? formattedMax;
  return `${formattedMin} - ${formattedMax}`;
}

export function formatWeight(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value) || value === 0) return null;
  return formatNumber(Math.round(value), { maximumFractionDigits: 0 });
}

export function formatDistance(value: number | null | undefined): string | null {
  return format(value, resolveUnit('distance'), 'miles');
}

export function convertDistanceToApiUnit(value: number): number {
  return convert(value, resolveUnit('distance').code, 'miles');
}
